/* (c) 2020-2023, Nextwave Logic, Inc. All Rights Reserved */

import React, { useState, useEffect, useContext } from 'react';
import SlideAlert from 'react-s-alert';
import { Link } from 'react-router-dom';
import { Button, Badge, Modal, ModalBody, ModalHeader, Alert } from 'reactstrap';
import Joyride, { STATUS } from 'react-joyride';
import moment from 'moment';

// context imports
import { AuthContext } from '../../contexts/AuthContext';
import { ThemeContext } from '../../contexts/ThemeContext';

// service imports
import GeneratePDF from "../../services/ReportGenerator";
import AuthService from '../../services/AuthService';
import ProviderService from '../../services/ProviderService';
import GeneralService from '../../services/GeneralService';

// local imports
import UserNotApproved from './UserNotApproved';
import ErrorBoundary from '../general/ErrorBoundary';
import ProviderNavbar from './ProviderNavbar';
import PaymentSession from './PaymentSession';
import Message from '../general/Message';

const Payments = props => {

    // contexts
    const { theme, colors } = useContext(ThemeContext);
    const { user, setUser, selectedTimeZone, userApprovalStatus } = useContext(AuthContext);

    // states
    const [isLoading, setIsLoading] = useState(false);
    const [expandAll, setExpandAll] = useState(false);
    const [detailModal, setDetailModal] = useState({ isVisible: false, data: { paymentInformation: {}, details: { sessionList: [] } } });
    const [paymentRecords, setPaymentRecords] = useState([]);
    const [clientSubscriptionPaymentRecords, setClientSubscriptionPaymentRecords] = useState([]);
    const [pendingAmount, setPendingAmount] = useState(0);
    const [totalEarned, setTotalEarned] = useState(0);
    const [profileComplete, setProfileComplete] = useState({
        status: true,
        message: {
            msgBody: [`Looks like your profile is incomplete! Please complete that by going to `, <b key="profileLink"><Link to='/counsellor/profile'>Profile</Link></b>, ` page to enjoy our service. Unless your profile is completed you won't be visible to any clients.`],
            msgError: true
        }
    });
    const [tutorial, setTutorial] = useState({
        status: false,
        steps: [
            {
                content: <h2>Details about your earnings will be shown here.</h2>,
                placement: 'center',
                target: '#paymentsDiv',
            },
            {
                content: <div className="tooltipDiv">Pending Amount shows how much Umang Wellness owes you.</div>,
                placement: 'bottom',
                target: '#pendingAmount'
            },
            {
                content: <div className="tooltipDiv">Total amount you earned is shown here.</div>,
                placement: 'bottom',
                target: '#totalEarned'
            },
            {
                content: <div className="tooltipDiv">Details about your earnings and payments to you for individual session are listed here.</div>,
                placement: 'top',
                target: '#sessionEarningTable'
            },
            {
                content: <div className="tooltipDiv">Details about your earnings and payments to you for subscriptions are listed here.</div>,
                placement: 'top',
                target: '#subscriptionEarningTable'
            },
        ]
    });
    let helpers = null;
    const [notifications, setNotifications] = useState({ newPaymentProcessed: [] });

    // useEffects
    useEffect(() => {
        ProviderService.paymentSummary()
            .then(async data => {
                // console.log('data', data);
                try {
                    if (!user.guideTakenFor.includes("Payments")) {
                        setTutorial({ ...tutorial, status: true });
                    }
                    setProfileComplete({ ...profileComplete, status: data.profileComplete });
                    setPaymentRecords(data.paymentInformation);
                    setClientSubscriptionPaymentRecords(data.subscriptionPaymentInformation);
                    let tempTotalEarned = 0;
                    let tempPendingAmount = 0;
                    await Promise.all(data.paymentInformation.map(item => {
                        if (item.isProcessed) {
                            tempTotalEarned += item.netAmountAfterDeductingRazorpayCharges;
                        } else {
                            tempPendingAmount += item.netAmountAfterDeductingRazorpayCharges;
                        }
                    }));
                    await Promise.all(data.subscriptionPaymentInformation.map(item => {
                        if (item.isProcessed) {
                            tempTotalEarned += item.netAmountAfterDeductingCharges;
                        } else {
                            tempPendingAmount += item.netAmountAfterDeductingCharges;
                        }
                    }));
                    setTotalEarned(tempTotalEarned);
                    setPendingAmount(tempPendingAmount);
                    // getDetails(data.paymentInformation[0]._id)
                } catch (err) {
                    SlideAlert.error("Some error occured, please try again later or report support team.", {
                        position: 'top-right',
                        effect: 'slide'
                    });
                    GeneralService.logError('at Provider Payments useEffect', 'Error in try catch block', err.stack);
                }
            })
        getNotifications();
    }, [])

    const getNotifications = () => {
        AuthService.getNotifications().then(data => {
            try {
                let temp = { newPaymentProcessed: [] };
                Promise.all(data.notifications.ProviderPayment.map(item => {
                    if (!item.readStatus && item.title === "newPaymentProcessed") {
                        temp.newPaymentProcessed.push(item.description);
                    }
                    AuthService.markNotificationAsRead("ProviderPayment", item._id);
                })).then(() => {
                    setNotifications(temp);
                });
            } catch (err) {
                SlideAlert.error("Some error occured, please try again later or report support team.", {
                    position: 'top-right',
                    effect: 'slide'
                });
                GeneralService.logError('at Provider Payments getNotifications', 'Error in try catch block', err.stack);
            }
        })
    }

    const getDetails = (id) => {
        // console.log(id);
        ProviderService.paymentDetail(id)
            .then(data => {
                // console.log(data);
                setDetailModal({ isVisible: true, data: data });
            })
    }

    // handle functions
    const handleSearch = () => {
        // todo
    }

    // useEffect(() => console.log(detailModal), [detailModal]);

    const handleExpandAll = () => {
        try {
            const tempMainArray = [...detailModal.data.details.sessionList];
            if (expandAll) {
                tempMainArray.map(item => item.isOpen = false);
            } else {
                tempMainArray.map(item => item.isOpen = true);
            }
            setExpandAll(!expandAll);
            setDetailModal({ ...detailModal, data: { ...detailModal.data, details: { ...detailModal.data.details, sessionList: tempMainArray } } });
        } catch (err) {
            SlideAlert.error("Some error occured, please try again later or report support team.", {
                position: 'top-right',
                effect: 'slide'
            });
            GeneralService.logError('at Provider Payments handleExpandAll', 'Error in try catch block', err.stack);
        }
    }

    const generateReport = () => {
        try {
            setIsLoading(true);
            GeneratePDF(detailModal.data.details.sessionList, detailModal.data.paymentInformation);
            setIsLoading(false);
        } catch (err) {
            SlideAlert.error("Some error occured, please try again later or report support team.", {
                position: 'top-right',
                effect: 'slide'
            });
            setIsLoading(false);
            GeneralService.logError('at Provider Payments generateReport', 'Error in try catch block', err.stack);
        }
    }

    const handleJoyrideCallback = (data) => {
        const { status, type } = data;
        const finishedStatuses = [STATUS.FINISHED, STATUS.SKIPPED];

        if (finishedStatuses.includes(status)) {
            if (!user.guideTakenFor.includes("Payments")) {
                AuthService.markVisited("Payments");
                setUser({ ...user, guideTakenFor: [...user.guideTakenFor, "Payments"] });
            }
            setTutorial({ ...tutorial, status: false });
        }
    };

    const getHelpers = (paramHelpers) => {
        helpers = paramHelpers;
    };

    // function's return
    return (
        <div id="paymentsDiv">
            <ErrorBoundary errorAt="At Provider Payments ProviderNavbar">
                <ProviderNavbar history={props.history} showTutorial={() => setTutorial({ ...tutorial, status: true })} />
            </ErrorBoundary>
            {
                userApprovalStatus ?
                    <div>
                        <ErrorBoundary errorAt="At Provider Payments Joyride">
                            <Joyride
                                callback={handleJoyrideCallback}
                                continuous={true}
                                getHelpers={getHelpers}
                                run={tutorial.status}
                                scrollToFirstStep={true}
                                showProgress={true}
                                showSkipButton={true}
                                steps={tutorial.steps}
                                locale={{ last: 'Finish' }}
                                styles={{
                                    options: {
                                        primaryColor: "rgb(56, 78, 115)",
                                        zIndex: 10000,
                                    },
                                }}
                            />
                        </ErrorBoundary>
                        {
                            !detailModal.isVisible ?
                                <div className="container-fluid mb-5">
                                    <div>
                                        <h1 style={theme.headingBold}>Payments</h1>
                                        <hr style={{ borderWidth: "2px" }} />
                                    </div>

                                    {profileComplete.status ? null : <Message message={profileComplete.message} />}

                                    <div className="my-4 col-md-8 offset-md-2">
                                        <div className="card">
                                            <div className="card-header">Payment Overview</div>
                                            <div className="card-body row p-3">
                                                <div className="col-md-5 p-5 m-4 text-center" style={theme.summaryCard} id="pendingAmount">
                                                    <span style={theme.summaryCardValue}>₹ {pendingAmount}</span>
                                                    <br />
                                                    <span style={theme.summaryCardTitle}>Pending Amount</span>
                                                </div>

                                                <div className="col-md-5 p-5 m-4 text-center" style={theme.summaryCard} id="totalEarned">
                                                    <span style={theme.summaryCardValue}>₹ {totalEarned}</span>
                                                    <br />
                                                    <span style={theme.summaryCardTitle}>Total Earned</span>
                                                </div>

                                                <Alert color="success" className="ml-4">Pending Amount is shown after deducting the razorpay charges</Alert>

                                                <Alert color="success" className="ml-4">Current Payment cycle is of 14 days.</Alert>

                                            </div>
                                        </div>
                                    </div>

                                    <div className="card mb-4">
                                        <div className="card-header">
                                            Payment Summary
                                        </div>
                                        <div className="card-body py-0">
                                            <ErrorBoundary errorAt="At Provider Payments PaymentSummaryTable">
                                                <div className="table-responsive">
                                                    <table className="table table-bordered mb-3" id="sessionEarningTable" width="100%" cellSpacing="0">
                                                        <thead>
                                                            <tr>
                                                                <td colSpan="6" className="border-0">
                                                                    <div className="d-flex">
                                                                        <div className="ml-auto">
                                                                            Search<input type="text" className="ml-2" onChange={handleSearch}></input>
                                                                        </div>
                                                                    </div>
                                                                </td>
                                                            </tr>
                                                            <tr>
                                                                <th>Dates</th>
                                                                <th>Amount Earned</th>
                                                                <th>Razorpay Charges</th>
                                                                <th>Net Amount after deducting razorpay charges</th>
                                                                <th>Paid on</th>
                                                                <th></th>
                                                            </tr>
                                                        </thead>
                                                        <tbody>
                                                            {
                                                                paymentRecords.map((item, index) => (
                                                                    <tr key={index}>
                                                                        <td>{moment(item.fromDate).utcOffset(selectedTimeZone.isDST ? selectedTimeZone.dst_offset : selectedTimeZone.offset).format("DD/MM/YYYY")} to {moment(item.toDate).utcOffset(selectedTimeZone.isDST ? selectedTimeZone.dst_offset : selectedTimeZone.offset).format("DD/MM/YYYY")}</td>
                                                                        <td>₹ {item.amountEarned}</td>
                                                                        <td>₹ {item.razorpayCharges}</td>
                                                                        <td>₹ {item.netAmountAfterDeductingRazorpayCharges}</td>
                                                                        <td>{!item.isProcessed ? `Expected on ${item.expectedPaymentDate}` : `${item.expectedPaymentDate}`}{notifications.newPaymentProcessed.includes(item._id) ? <Badge className="ml-1" style={{ backgroundColor: colors.warning }}>New</Badge> : null}</td>
                                                                        <td align="center"><a style={{ color: "#384E73" }} href="#" onClick={() => getDetails(item._id)}>View details</a></td>
                                                                    </tr>
                                                                ))
                                                            }
                                                        </tbody>

                                                    </table>
                                                </div>
                                            </ErrorBoundary>
                                        </div>
                                    </div>

                                    <div className="card mb-4">
                                        <div className="card-header">
                                            Subscription Payment Summary
                                        </div>
                                        <div className="card-body py-0">
                                            <ErrorBoundary errorAt="At Provider Payments SubscriptionPaymentSummaryTable">
                                                <div className="table-responsive">
                                                    <table className="table table-bordered mb-3" id="subscriptionEarningTable" width="100%" cellSpacing="0">
                                                        <thead>
                                                            <tr>
                                                                <td colSpan="5" className="border-0">
                                                                    <div className="d-flex">
                                                                        <div className="ml-auto">
                                                                            Search<input type="text" className="ml-2" /*onChange={handleSearch}*/></input>
                                                                        </div>
                                                                    </div>
                                                                </td>
                                                            </tr>
                                                            <tr>
                                                                <th>Dates</th>
                                                                <th>Amount Earned</th>
                                                                <th>Razorpay Charges</th>
                                                                <th>Net Amount after deducting razorpay charges</th>
                                                                <th>Paid on</th>
                                                                {/* <th></th> */}
                                                            </tr>
                                                        </thead>
                                                        <tbody>
                                                            {
                                                                clientSubscriptionPaymentRecords.map((item, index) => (
                                                                    <tr key={index}>
                                                                        <td>{moment(item.fromDate).utcOffset(selectedTimeZone.isDST ? selectedTimeZone.dst_offset : selectedTimeZone.offset).format("DD/MM/YYYY")} to {moment(item.toDate).utcOffset(selectedTimeZone.isDST ? selectedTimeZone.dst_offset : selectedTimeZone.offset).format("DD/MM/YYYY")}</td>
                                                                        <td>₹ {item.amountEarned}</td>
                                                                        <td>₹ {item.razorpayCharges}</td>
                                                                        <td>₹ {item.netAmountAfterDeductingCharges}</td>
                                                                        <td>{!item.isProcessed ? `Expected on ${item.expectedPaymentDate}` : `${item.expectedPaymentDate}`}{/*notifications.newPaymentProcessed.includes(item._id) ? <Badge className="ml-1" style={{ backgroundColor: colors.warning }}>New</Badge> : null*/}</td>
                                                                        {/* <td align="center"><a style={{ color: "#384E73" }} href="#">View details</a></td> */}
                                                                    </tr>
                                                                ))
                                                            }
                                                        </tbody>

                                                    </table>
                                                </div>
                                            </ErrorBoundary>
                                        </div>
                                    </div>
                                </div>
                                :
                                <div className="container-fluid mb-5">
                                    <ErrorBoundary errorAt="At Provider Payments PaymentDetail">
                                        <div className="d-flex my-4">
                                            <h3 className="my-auto" style={{ marginRight: "15px", cursor: "pointer" }} onClick={() => { setDetailModal({ isVisible: false, data: { paymentInformation: {}, details: { sessionList: [] } } }); setExpandAll(false); }}><i className="fas fa-arrow-left"></i></h3>
                                            <h3 className="my-auto">{detailModal.data.paymentInformation.fromDate} to {detailModal.data.paymentInformation.toDate}</h3>
                                            <h5 className="ml-auto my-auto" style={{ cursor: "pointer" }} onClick={handleExpandAll}>{expandAll ? "Collapse all" : "Expand all"}</h5>
                                            <Button className="ml-2" style={theme.button} onClick={() => generateReport()} disabled={isLoading}>{isLoading ? <span className="spinner-border spinner-border-sm mr-1"></span> : null}Report</Button>
                                        </div>
                                    </ErrorBoundary>
                                    {
                                        detailModal.data.details.sessionList.map((session, index) => (
                                            <ErrorBoundary errorAt={`At Payments PaymentSession${index}`}>
                                                <PaymentSession isOpen={session.isOpen} index={index} detail={session} gstPercentage={detailModal.data.paymentInformation.gstPercentage} selectedTimeZone={selectedTimeZone} />
                                            </ErrorBoundary>
                                        ))
                                    }
                                </div>
                        }
                    </div>
                    :
                    <UserNotApproved />
            }
        </div>
    )

}

export default Payments;